if (!customElements.get('product-external-video')) {
  class ProductExternalVideo extends HTMLElement {
    constructor() {
      super();
      this.handlePosterClick = this.handlePosterClick.bind(this);
    }

    connectedCallback() {
      this.posterButton = this.querySelector('[data-video-poster]');
      this.embedContainer = this.querySelector('[data-video-embed]');
      this.videoUrl = this.dataset.videoUrl;
      this.videoHost = this.dataset.videoHost;
      this.videoTitle = this.dataset.videoTitle;
      this.autoPlay = this.dataset.autoplay === 'true';

      if (!this.posterButton || !this.embedContainer || !this.videoUrl) return;

      if (!this.dataset.initialized) {
        this.posterButton.addEventListener('click', this.handlePosterClick);
        this.dataset.initialized = 'true';
      }

      if (this.autoPlay) {
        this.loadIframe(true);
      }
    }

    disconnectedCallback() {
      this.posterButton?.removeEventListener('click', this.handlePosterClick);
    }

    handlePosterClick() {
      this.loadIframe(true);
    }

    loadIframe(forceAutoplay = false) {
      if (!this.embedContainer || this.embedContainer.childElementCount > 0) return;

      let autoplayParam = '';
      if (forceAutoplay) {
        if (this.videoHost === 'youtube') {
          autoplayParam = this.videoUrl.includes('?')
            ? '&autoplay=1&mute=1&playsinline=1'
            : '?autoplay=1&mute=1&playsinline=1';
        } else if (this.videoHost === 'vimeo') {
          autoplayParam = this.videoUrl.includes('?')
            ? '&autoplay=1&muted=1&background=1&loop=1&quality=720p'
            : '?autoplay=1&muted=1&background=1&loop=1&quality=720p';
        }
      }

      const iframe = document.createElement('iframe');
      iframe.src = this.videoUrl + autoplayParam;
      iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
      iframe.setAttribute('allowfullscreen', 'true');
      iframe.setAttribute('title', this.videoTitle);
      iframe.setAttribute('frameborder', '0');
      iframe.loading = 'eager';

      this.embedContainer.appendChild(iframe);
      this.embedContainer.hidden = false;

      // Vimeo background → remove poster only when playback starts
      if (this.videoHost === 'vimeo' && window.Vimeo) {
        const player = new Vimeo.Player(iframe);
        player.on('play', () => this.removePoster());
      }

      // YouTube fallback → remove poster once iframe loads
      if (this.videoHost === 'youtube') {
        iframe.addEventListener('load', () => this.removePoster(), { once: true });
      }
    }

    removePoster() {
      const poster = this.posterButton;
      if (!poster) return;

      poster.classList.add('is-fading');
      poster.style.pointerEvents = 'none';

      const remove = () => {
        if (!poster.isConnected) return;
        poster.remove();
        if (this.posterButton === poster) {
          this.posterButton = null;
        }
      };

      poster.addEventListener('transitionend', remove, { once: true });
      setTimeout(remove, 400);
    }
  }

  customElements.define('product-external-video', ProductExternalVideo);
}

class ProductBuyBar extends HTMLElement {
  constructor() {
    "use strict";
    super();

    let classes = { show: "show" };
    let mainProductForm = document.querySelector(".product-form");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // In view -> Hide bar
            this.classList.remove(classes.show);
          } else if (window.scrollY > 200) {
            // Out of view AND scrolled a little from top -> Show bar
            this.classList.add(classes.show);
          }
        });
      },
      { threshold: 0.25 }
    );

    observer.observe(mainProductForm);

    // This script is loaded in product-template.liquid --> Footer observer after DOM ready
    document.addEventListener("DOMContentLoaded", function () {
      let siteFooter = document.querySelector("footer.footer");
      if (!siteFooter) return;
      observer.observe(siteFooter);
    });
  }
}

customElements.define("product-buy-bar", ProductBuyBar);

class UpsellCard extends HTMLElement {
  constructor() {
    super();
    this.select = this.querySelector('select');
    this.image = this.querySelector('.card-media img');

    if (this.select) {
      this.select.addEventListener('change', (event) => {
        const selectedOption = event.target.options[event.target.selectedIndex];
        const newImgSrc = selectedOption.getAttribute('data-variant-img-src');
        if (newImgSrc) { this.updateImage(newImgSrc) }
      });
    }
  }

  updateImage(newImgSrc) {
    if (this.image && newImgSrc) {
      // Sanitize and validate URL to prevent XSS
      // Remove any whitespace and HTML entities
      const sanitized = String(newImgSrc).trim().replace(/[\s<>"']/g, '');

      // Reject dangerous protocols (check anywhere in string, not just start)
      const dangerousProtocols = /^(javascript|data|vbscript|file|about):/i;
      if (dangerousProtocols.test(sanitized)) {
        return; // Reject dangerous URLs
      }

      try {
        // Try to parse as absolute URL
        const url = new URL(sanitized, window.location.origin);
        // Only allow http and https protocols
        if (url.protocol === 'http:' || url.protocol === 'https:') {
          // Use the properly constructed URL to prevent any encoding issues
          this.image.src = url.href;
        }
      } catch (e) {
        // If URL parsing fails, it's likely a relative URL
        // Additional validation: ensure it doesn't contain dangerous patterns
        if (!sanitized.includes('javascript:') &&
            !sanitized.includes('data:') &&
            !sanitized.includes('vbscript:') &&
            !sanitized.includes('<') &&
            !sanitized.includes('>')) {
          // For relative URLs, ensure proper encoding
          this.image.src = sanitized;
        }
      }
    }
  }
}

customElements.define('upsell-card', UpsellCard);

// Bulk add-to-cart for upsell cards
class UpsellBulkAdd extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
  }

  connectedCallback() {
    this.button = this.querySelector('[data-add-all]');
    this.button?.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
  }

  getItems() {
    return [...this.querySelectorAll('upsell-card')]
      .map(card => {
        const variantId =
          card.querySelector('select.upsell-id, .select__select.upsell-id')?.value ||
          card.querySelector('input.upsell-id-separate')?.value;
        return variantId ? { id: parseInt(variantId, 10), quantity: 1 } : null;
      })
      .filter(Boolean);
  }

  setBusy(busy) {
    if (!this.button) return;

    let loadingOverlay = this.button.querySelector('.loading-overlay');
    this.button.toggleAttribute('disabled', busy);
    this.button.classList.toggle('loading', busy);
    loadingOverlay.classList.toggle('hidden', !busy);
  }

  async onClick() {
    const items = this.getItems();
    if (!items.length) return;

    this.setBusy(true);
    try {
      const res = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      });

      if (!res.ok) throw await res.json();
      const data = await res.json();
      const addedItems = Array.isArray(data?.items) ? data.items : items;
      const detailItems = addedItems.map((item) => ({
        variantId: item.variant_id || item.id,
        productId: item.product_id,
        quantity: item.quantity || 1,
        productTitle: item.product_title || item.title || '',
        title: item.title || item.product_title || ''
      }));

      document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { items: detailItems } }));
    } catch (e) {
      console.error('Upsell bulk add failed', e);
    } finally {
      this.setBusy(false);
    }
  }
}

customElements.define('upsell-bulk-add', UpsellBulkAdd);
